import { useEffect, useState } from "react";
import { WarningCircle, X } from "@phosphor-icons/react";

/**
 * Vincent 2026-05-04 — bandeau global de statut de connexion serveur.
 *
 * Affiche un message clair quand une erreur 5xx ou réseau survient, pour
 * éviter que l'utilisateur croie à une déconnexion. Disparaît automatiquement
 * après 8 s ou via le bouton X. Écoute l'événement DOM `knoria:server-error`
 * publié par l'interceptor axios dans `AuthContext.js`.
 */
export default function ConnectionStatusBanner() {
  const [event, setEvent] = useState(null); // { kind, status, url, ts }
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    // Eric 2026-05-05 — anti-spam : si plus de 3 erreurs en 30s, on cache pendant
    // 60s pour éviter le bandeau collant qui réapparaît toutes les 8s.
    let recentErrors = []; // timestamps
    let suppressUntil = 0;

    const onError = (e) => {
      const now = Date.now();
      if (now < suppressUntil) return;
      recentErrors = recentErrors.filter((t) => now - t < 30000);
      recentErrors.push(now);
      if (recentErrors.length > 3) {
        suppressUntil = now + 60000;
        return;
      }
      const detail = e.detail || {};
      setEvent({ ...detail, ts: now });
      setHidden(false);
    };
    window.addEventListener("knoria:server-error", onError);
    return () => window.removeEventListener("knoria:server-error", onError);
  }, []);

  useEffect(() => {
    if (!event || hidden) return;
    const timer = setTimeout(() => setHidden(true), 8000);
    return () => clearTimeout(timer);
  }, [event, hidden]);

  if (!event || hidden) return null;

  const isServer = event.kind === "server";
  const statusLabel = isServer
    ? `Erreur serveur (${event.status}) — l'équipe Knoria a été notifiée`
    : "Connexion temporairement indisponible — réessayez dans quelques secondes";

  return (
    <div
      className="fixed top-2 left-1/2 -translate-x-1/2 z-[60] px-4 py-2.5 rounded-lg shadow-lg border bg-amber-50 border-amber-300 text-amber-900 flex items-center gap-2 max-w-2xl"
      data-testid="connection-status-banner"
      role="alert"
    >
      <WarningCircle size={18} weight="fill" className="text-amber-600 flex-shrink-0" />
      <div className="flex-1 text-xs leading-snug">
        <p className="font-semibold">{statusLabel}</p>
        <p className="text-[11px] opacity-80">
          Vous <strong>restez connecté</strong>. Pas besoin de vous reconnecter.
        </p>
      </div>
      <button
        type="button"
        onClick={() => setHidden(true)}
        className="p-1 hover:bg-amber-100 rounded transition-colors"
        data-testid="connection-banner-close"
        aria-label="Fermer"
      >
        <X size={14} />
      </button>
    </div>
  );
}
